import {vratiNormativeZaStavku} from '../../../../api/fmkNalozi'
import {jelStavkaZaduzena} from '../../../../api/radniNalozi'
import {FmkNalogiStavkeModel} from '../../../../models/FmkNaloziModel'
import type {MaterijalTip} from './Step3'

const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const extractMaterialId = (raw: string) => normalizeScan(raw).split(';')[0].trim()

const getStavkaId = (item: any) => item?.IdStavke ?? item?.IdStavkeFMK ?? item?.Id ?? item?.id

const getBrojNalogaFromInfo = (info: any) =>
    info?.brojNaloga ?? info?.BrojNaloga ?? info?.data?.brojNaloga ?? info?.data?.BrojNaloga

const parseNumber = (value: any) => {
    if (typeof value === 'number') return value

    const normalized = String(value ?? '').trim()
    if (!normalized) return NaN

    if (normalized.includes(',') && normalized.includes('.')) {
        return Number(normalized.replace(/\./g, '').replace(',', '.'))
    }

    if (normalized.includes(',')) {
        return Number(normalized.replace(',', '.'))
    }

    return Number(normalized)
}

const getZaduzenjePayload = (response: any) => {
    if (response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
        return response.data.data
    }

    if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return response.data
    }

    return response
}

const getZaduzenjeRow = (response: any) => {
    const data = getZaduzenjePayload(response)
    if (!data) return null
    if (Array.isArray(data)) return data.length ? data[0] : null
    return data
}

const getZaduzenjeUposlenik = (data: any) =>
    data?.uposlenik?.UposlenikImePrezime ??
    data?.Uposlenik?.UposlenikImePrezime ??
    data?.uposlenikImePrezime ??
    data?.UposlenikImePrezime ??
    '-'

const checkZaduzenje = async (materijalId: string, tip: MaterijalTip) => {
    const response = await jelStavkaZaduzena({
        rolna: tip === 'rolna' ? 1 : 0,
        ...(tip === 'rolna' ? {RolnaId: materijalId} : {TrakaId: materijalId}),
    })

    return getZaduzenjeRow(response)
}

const getMaterijalNormativSastojak = (materijal: any, tip: MaterijalTip = 'rolna') => {
    const rolna = tip === 'traka' ? materijal?.repromaterijal : materijal
    if (!rolna) return ''

    return rolna.ReprMatFmkSifra ?? rolna.reprMatFmkSifra ?? ''
}

const getResponseData = (response: any): any => {
    if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return getResponseData(response.data)
    }
    return response
}

const getNormativAmount = (response: any) => {
    const data = getResponseData(response)
    const row = Array.isArray(data) ? data[0] : data

    if (!row || row?.normativ === null || row?.Normativ === null) {
        return {
            amount: 0,
            warning: row?.napomena ?? row?.message ?? response?.message ??
                'Odabrana rolna / traka ne odgovara normativu. Molim da se usaglasite sa rukovodiocem.',
        }
    }

    return {
        amount: parseNumber(
            row?.normativ?.kolicinaSastojkaUProizvodu ??
            row?.normativ?.KolicinaSastojkaUProizvodu ??
            row?.Normativ?.kolicinaSastojkaUProizvodu ??
            row?.Normativ?.KolicinaSastojkaUProizvodu ??
            row?.kolicinaSastojkaUProizvodu ??
            row?.KolicinaSastojkaUProizvodu ??
            row?.kolicina_sastojka_u_proizvodu
        ),
        warning: '',
    }
}

const getStavkaNormativParams = (stavka: any, idSastojak: any, stavkaInfo: Record<string, any>) => {
    const stavkaId = getStavkaId(stavka)
    const info = stavkaInfo[String(stavkaId)] ?? {}

    return {
        idStavkaNarudzbeFmk:
            stavka?.fmkId ??
            stavka?.FmkId ??
            info?.FmkIdStavke ??
            info?.data?.FmkIdStavke ??
            stavka?.FmkIdStavke ??
            stavkaId,
        idArtikla:
            stavka?.SifraArtikla ??
            stavka?.IdArtikla ??
            info?.SifraArtikla ??
            info?.data?.SifraArtikla,
        idSastojak,
    }
}

const calculatePotrebnaKolicina = async (materijal: any, tip: MaterijalTip, selectedStavka: FmkNalogiStavkeModel['stavka'], stavkaInfo: Record<string, any>) => {
    const idSastojak = getMaterijalNormativSastojak(materijal, tip)
    if (!idSastojak || !selectedStavka.length) {
        throw new Error('Nije moguće izračunati normativ za odabrani materijal.')
    }

    const rows = await Promise.all(
        selectedStavka.map(async (stavka: any) => {
            const stavkaId = getStavkaId(stavka)
            const response = await vratiNormativeZaStavku(getStavkaNormativParams(stavka, idSastojak, stavkaInfo))
            const {amount: normativAmount, warning} = getNormativAmount(response)
            const stavkaKolicina = parseNumber(stavka?.Kolicina)
            if (!Number.isFinite(normativAmount) || !Number.isFinite(stavkaKolicina)) {
                throw new Error('Nije moguće izračunati potrebnu količinu za odabranu stavku.')
            }
            const potrebnaKolicina = Math.round(normativAmount * stavkaKolicina)

            return [String(stavkaId), potrebnaKolicina, warning] as const
        })
    )

    const poStavci = rows.reduce<Record<string, number>>((acc, [stavkaId, potrebnaKolicina]) => {
        acc[stavkaId] = potrebnaKolicina
        return acc
    }, {})
    const total = rows.reduce((sum, [, potrebnaKolicina]) => sum + potrebnaKolicina, 0)

    return {
        total,
        poStavci,
        warning: Array.from(new Set(rows.map(([, , warning]) => warning).filter(Boolean))).join(' '),
    }
}


export {normalizeScan, extractMaterialId, getStavkaId, getBrojNalogaFromInfo, checkZaduzenje, getZaduzenjeUposlenik, getResponseData, calculatePotrebnaKolicina}
